import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Package, Plus, PencilSimple, Trash, MagnifyingGlass,
  Funnel, FloppyDisk, X, Check, Warning, Sparkle
} from "@phosphor-icons/react";
import { api } from "../../utils/api";
import { comprimirImagen } from "../../utils/imagenes";
import { Button } from "../shared/Button";
import { Input, Textarea, Select } from "../shared/Input";
import { Modal } from "../shared/Modal";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Badge } from "../shared/Badge";

const formatCurrency = (val) => new Intl.NumberFormat("es-ES", { 
  style: "currency", 
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
}).format(val);

export const GestorMateriales = ({ adminToken }) => {
  const [materiales, setMateriales] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEditar, setModalEditar] = useState(false);
  const [materialEditado, setMaterialEditado] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("todos");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [materialesData, modulosData] = await Promise.all([
        api.get("/materiales", adminToken),
        api.get("/modulos")
      ]);
      setMateriales(materialesData);
      setModulos(modulosData);
    } catch (e) {
      console.error("Error cargando datos:", e);
      alert("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const cargarMateriales = async () => {
    setLoading(true);
    try {
      const data = await api.get("/materiales", adminToken);
      setMateriales(data);
    } catch (e) {
      console.error("Error cargando materiales:", e);
      alert("Error al cargar materiales");
    } finally {
      setLoading(false);
    }
  };

  const handleFotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const comprimida = await comprimirImagen(file);
      setMaterialEditado((p) => ({ ...p, foto_url: comprimida }));
    } catch (err) {
      console.error("Error comprimiendo imagen:", err);
      alert("No se pudo procesar la foto — probá con otra.");
    }
  };

  const abrirEditar = (material) => {
    setMaterialEditado(JSON.parse(JSON.stringify(material))); // Deep clone
    setModalEditar(true);
  };

  const abrirNuevo = () => {
    setMaterialEditado({
      nombre: "",
      categoria: "pintura",
      modulo: "pintura_paramentos",
      unidad: "L",
      precio_compra: 0,
      precio_eco: 0,
      precio_std: 0,
      precio_prem: 0,
      marca: "",
      referencia: "",
      color: "",
      descripcion: "",
      foto_url: "",
      activo: true
    });
    setModalEditar(true);
  };

  const guardarMaterial = async () => {
    if (!materialEditado.nombre || !materialEditado.categoria) {
      alert("⚠️ El nombre y categoría son obligatorios");
      return;
    }

    setGuardando(true);
    try {
      if (materialEditado.id) {
        // Editar existente
        await api.put(`/admin/materiales/${materialEditado.id}`, materialEditado, adminToken);
        alert("✅ Material actualizado correctamente");
      } else {
        // Crear nuevo
        await api.post("/materiales", materialEditado, adminToken);
        alert("✅ Material creado correctamente");
      }
      setModalEditar(false);
      setMaterialEditado(null);
      cargarMateriales();
    } catch (error) {
      console.error("Error guardando material:", error);
      alert("❌ Error al guardar material: " + (error.message || "Error desconocido"));
    } finally {
      setGuardando(false);
    }
  };

  const eliminarMaterial = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar material "${nombre}"?\n\n⚠️ Esta acción no se puede deshacer.`)) return;
    
    try {
      await api.delete(`/materiales/${id}`, adminToken);
      alert("✅ Material eliminado correctamente");
      cargarMateriales();
    } catch (error) {
      console.error("Error eliminando material:", error);
      alert("❌ Error al eliminar material: " + (error.message || "Error desconocido"));
    }
  };

  const toggleActivo = async (material) => {
    try {
      await api.put(`/admin/materiales/${material.id}`, { ...material, activo: !material.activo }, adminToken);
      cargarMateriales();
    } catch (error) {
      console.error("Error actualizando estado:", error);
      alert("❌ Error al cambiar estado del material");
    }
  };

  // Filtros
  const materialesFiltrados = materiales.filter(m => {
    const matchBusqueda = m.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
                          (m.marca || "").toLowerCase().includes(busqueda.toLowerCase()) ||
                          (m.descripcion || "").toLowerCase().includes(busqueda.toLowerCase());
    const matchCategoria = filtroCategoria === "todos" || m.categoria === filtroCategoria || m.modulo === filtroCategoria;
    return matchBusqueda && matchCategoria;
  });

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-h2 font-display flex items-center gap-2">
              <Package size={28} weight="fill" style={{ color: "var(--brand-accent)" }} />
              Gestor de Materiales
            </h1>
            <p className="text-small text-secondary">
              {materiales.length} materiales registrados · {materialesFiltrados.length} mostrando
            </p>
          </div>
          <Button variant="accent" onClick={abrirNuevo} icon={<Plus size={20} />}>
            Nuevo Material
          </Button>
        </div>

        {/* Búsqueda y filtros */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por nombre, marca o descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              icon={<MagnifyingGlass size={18} />}
            />
          </div>
          <Select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            options={[
              { value: "todos", label: "Todas las categorías" },
              ...modulos.map(mod => ({ value: mod.id, label: `${mod.icono} ${mod.nombre}` }))
            ]}
            icon={<Funnel size={18} />}
          />
        </div>

        {/* Lista de materiales */}
        {materialesFiltrados.length === 0 ? (
          <EmptyState
            icon={<Package size={48} />}
            title="No hay materiales"
            description={busqueda || filtroCategoria !== "todos" 
              ? "No se encontraron materiales con los filtros aplicados" 
              : "Añade el primer material para empezar"}
            action={<Button variant="accent" onClick={abrirNuevo}>Añadir Material</Button>}
          />
        ) : (
          <div className="space-y-3">
            {materialesFiltrados.map(material => (
              <motion.div
                key={material.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {material.foto_url ? (
                      <img
                        src={material.foto_url}
                        alt={material.nombre}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ background: "var(--brand-primary)", color: "var(--brand-accent)" }}
                      >
                        <Package size={24} weight="fill" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Nombre y estado */}
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-bold text-body">{material.nombre}</div>
                      {!material.activo && (
                        <Badge variant="outline" size="sm">Inactivo</Badge>
                      )}
                    </div>

                    {/* Info básica */}
                    <div className="text-small text-secondary mb-2">
                      <span className="font-medium">{material.categoria}</span>
                      {material.marca && <> · {material.marca}</>}
                      {material.referencia && <> · Ref: {material.referencia}</>}
                    </div>

                    {material.descripcion && (
                      <div className="text-tiny text-secondary mb-2 italic">
                        {material.descripcion}
                      </div>
                    )}

                    {/* Precios */}
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      <div>
                        <div className="text-tiny text-secondary">Compra</div>
                        <div className="text-small font-bold">{formatCurrency(material.precio_compra || 0)}</div>
                      </div>
                      <div>
                        <div className="text-tiny" style={{ color: "#22c55e" }}>💚 ECO</div>
                        <div className="text-small font-bold" style={{ color: "#22c55e" }}>
                          {formatCurrency(material.precio_eco || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-tiny" style={{ color: "#3b82f6" }}>🔵 STD</div>
                        <div className="text-small font-bold" style={{ color: "#3b82f6" }}>
                          {formatCurrency(material.precio_std || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-tiny" style={{ color: "#f97316" }}>🟠 PREM</div>
                        <div className="text-small font-bold" style={{ color: "#f97316" }}>
                          {formatCurrency(material.precio_prem || 0)}
                        </div>
                      </div>
                    </div>

                    {/* Márgenes */}
                    <div className="flex gap-3 mt-2 text-tiny text-secondary">
                      <div>Margen ECO: {material.precio_eco && material.precio_compra 
                        ? `${(((material.precio_eco - material.precio_compra) / material.precio_compra) * 100).toFixed(0)}%` 
                        : '-'}
                      </div>
                      <div>Margen STD: {material.precio_std && material.precio_compra 
                        ? `${(((material.precio_std - material.precio_compra) / material.precio_compra) * 100).toFixed(0)}%` 
                        : '-'}
                      </div>
                      <div>Margen PREM: {material.precio_prem && material.precio_compra 
                        ? `${(((material.precio_prem - material.precio_compra) / material.precio_compra) * 100).toFixed(0)}%` 
                        : '-'}
                      </div>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => abrirEditar(material)}
                      icon={<PencilSimple size={16} />}
                    >
                      Editar
                    </Button>
                    <Button
                      variant={material.activo ? "outline" : "success"}
                      size="sm"
                      onClick={() => toggleActivo(material)}
                      icon={material.activo ? <X size={16} /> : <Check size={16} />}
                    >
                      {material.activo ? "Desactivar" : "Activar"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => eliminarMaterial(material.id, material.nombre)}
                      icon={<Trash size={16} />}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Editar/Nuevo */}
      {modalEditar && materialEditado && (
        <Modal
          isOpen={modalEditar}
          onClose={() => setModalEditar(false)}
          title={materialEditado.id ? `Editar: ${materialEditado.nombre}` : "Nuevo Material"}
        >
          <div className="space-y-4">
            {/* Información básica */}
            <div>
              <label className="text-label mb-2 block">Nombre del Material *</label>
              <Input
                placeholder="Ej: Pintura plástica blanca mate Bruguer Pyrineos 10L"
                value={materialEditado.nombre}
                onChange={(e) => setMaterialEditado({ ...materialEditado, nombre: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-label mb-2 block">Categoría *</label>
                <Select
                  value={materialEditado.categoria}
                  onChange={(e) => setMaterialEditado({ ...materialEditado, categoria: e.target.value })}
                  options={[
                    { value: "pintura", label: "Pintura" },
                    { value: "suelos", label: "Suelos" },
                    { value: "revestimientos", label: "Revestimientos" },
                    { value: "cerámica", label: "Cerámica" },
                    { value: "tabiquería", label: "Tabiquería / Pladur" },
                    { value: "fontanería", label: "Fontanería" },
                    { value: "electricidad", label: "Electricidad" },
                    { value: "carpintería", label: "Carpintería" },
                    { value: "metal", label: "Metal" },
                    { value: "baño", label: "Baño" },
                    { value: "cocina", label: "Cocina" },
                    { value: "herrajes", label: "Herrajes" },
                    { value: "otros", label: "Otros" },
                  ]}
                />
              </div>
              <div>
                <label className="text-label mb-2 block">Unidad</label>
                <Select
                  value={materialEditado.unidad}
                  onChange={(e) => setMaterialEditado({ ...materialEditado, unidad: e.target.value })}
                  options={[
                    { value: "L", label: "Litros (L)" },
                    { value: "kg", label: "Kilogramos (kg)" },
                    { value: "m2", label: "Metro cuadrado (m²)" },
                    { value: "ml", label: "Metro lineal (ml)" },
                    { value: "ud", label: "Unidad (ud)" },
                    { value: "caja", label: "Caja" },
                    { value: "bote", label: "Bote" },
                  ]}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Marca"
                placeholder="Ej: Bruguer"
                value={materialEditado.marca || ""}
                onChange={(e) => setMaterialEditado({ ...materialEditado, marca: e.target.value })}
              />
              <Input
                label="Referencia"
                placeholder="Ej: PIN-PLB-10L"
                value={materialEditado.referencia || ""}
                onChange={(e) => setMaterialEditado({ ...materialEditado, referencia: e.target.value })}
              />
            </div>

            <Input
              label="Color / tono (para que el cliente filtre en el catálogo)"
              placeholder="Ej: Roble natural, Blanco mate, Gris antracita"
              value={materialEditado.color || ""}
              onChange={(e) => setMaterialEditado({ ...materialEditado, color: e.target.value })}
            />

            <Textarea
              label="Descripción"
              placeholder="Descripción detallada del material, características, usos..."
              value={materialEditado.descripcion || ""}
              onChange={(e) => setMaterialEditado({ ...materialEditado, descripcion: e.target.value })}
              rows={3}
            />

            <div>
              <label className="text-label mb-2 block">Foto real del material</label>
              <div className="text-tiny text-secondary mb-2">
                Foto real (de catálogo del proveedor, del envase, o de una obra ya hecha) — se
                usa para mostrarle al cliente y para el Asistente en Obra. No subas imágenes
                genéricas de internet, tiene que ser lo que realmente instalás.
              </div>
              <div className="flex items-center gap-3">
                {materialEditado.foto_url && (
                  <img
                    src={materialEditado.foto_url}
                    alt="Foto del material"
                    className="w-20 h-20 rounded-lg object-cover"
                    style={{ border: "1px solid var(--border)" }}
                  />
                )}
                <div className="flex-1 flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFotoChange}
                    className="text-tiny"
                  />
                  {materialEditado.foto_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMaterialEditado({ ...materialEditado, foto_url: "" })}
                      icon={<Trash size={14} />}
                    >
                      Quitar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Precios */}
            <div className="card p-4" style={{ background: "#FFF8E1", border: "1px solid #F59E0B" }}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkle size={20} style={{ color: "#F59E0B" }} />
                <div className="font-bold text-small">Precios y Márgenes</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-label mb-2 block">Precio Compra (€)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={materialEditado.precio_compra || ""}
                    onChange={(e) => setMaterialEditado({ ...materialEditado, precio_compra: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                  />
                </div>
                <div className="text-tiny text-secondary pt-6">
                  Base para calcular márgenes de beneficio
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3">
                <div>
                  <label className="text-label mb-2 block" style={{ color: "#22c55e" }}>💚 ECO (€)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={materialEditado.precio_eco || ""}
                    onChange={(e) => setMaterialEditado({ ...materialEditado, precio_eco: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                  />
                  {materialEditado.precio_compra > 0 && materialEditado.precio_eco > 0 && (
                    <div className="text-tiny text-secondary mt-1">
                      +{(((materialEditado.precio_eco - materialEditado.precio_compra) / materialEditado.precio_compra) * 100).toFixed(0)}% margen
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-label mb-2 block" style={{ color: "#3b82f6" }}>🔵 STD (€)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={materialEditado.precio_std || ""}
                    onChange={(e) => setMaterialEditado({ ...materialEditado, precio_std: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                  />
                  {materialEditado.precio_compra > 0 && materialEditado.precio_std > 0 && (
                    <div className="text-tiny text-secondary mt-1">
                      +{(((materialEditado.precio_std - materialEditado.precio_compra) / materialEditado.precio_compra) * 100).toFixed(0)}% margen
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-label mb-2 block" style={{ color: "#f97316" }}>🟠 PREM (€)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={materialEditado.precio_prem || ""}
                    onChange={(e) => setMaterialEditado({ ...materialEditado, precio_prem: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                  />
                  {materialEditado.precio_compra > 0 && materialEditado.precio_prem > 0 && (
                    <div className="text-tiny text-secondary mt-1">
                      +{(((materialEditado.precio_prem - materialEditado.precio_compra) / materialEditado.precio_compra) * 100).toFixed(0)}% margen
                    </div>
                  )}
                </div>
              </div>

              <div className="card p-2 mt-3" style={{ background: "rgba(255,255,255,0.6)" }}>
                <div className="text-tiny text-secondary">
                  💡 <strong>Recomendación:</strong> ECO +20-30%, STD +40-50%, PREMIUM +60-80% sobre precio de compra
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setModalEditar(false)}
                disabled={guardando}
              >
                Cancelar
              </Button>
              <Button
                variant="success"
                className="flex-1"
                onClick={guardarMaterial}
                disabled={guardando || !materialEditado.nombre || !materialEditado.categoria}
                icon={<FloppyDisk size={20} />}
              >
                {guardando ? "Guardando..." : (materialEditado.id ? "Actualizar" : "Crear")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
